import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCrypto } from '../hooks';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { arrayBufferToBase64 } from '../lib/crypto';
import { applyAccountToSession, saveOrUpdateAccount } from '../lib/session';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type RegisterStep = 'username' | 'passkey' | 'passphrase';

export function RegisterPage() {
  const navigate = useNavigate();
  const { generateIdentityKeys, encryptPrivateKeyWithPassphrase } = useCrypto();

  const [step, setStep] = useState<RegisterStep>('username');
  const [username, setUsername] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyCredential, setPasskeyCredential] = useState<{
    id: string;
    publicKeySpki: string;
    publicKeyAlgorithm: number;
    clientDataJSON: string;
    authenticatorData: string;
  } | null>(null);
  const [webAuthnOptions, setWebAuthnOptions] = useState<{
    challenge: string;
    rp: { id: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: Array<{ type: string; alg: number }>;
  } | null>(null);

  const handleUsernameSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await api.auth.passkeyRegisterBegin({ username });
      setWebAuthnOptions({
        challenge: result.challenge,
        rp: result.rp,
        user: result.user,
        pubKeyCredParams: result.pubKeyCredParams
      });
      setStep('passkey');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('エラーが発生しました', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyRegister = async () => {
    if (!webAuthnOptions) {
      setError(t('WebAuthnオプションの取得に失敗しました', 'WebAuthn options not loaded'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const challenge = Uint8Array.from(
        atob(webAuthnOptions.challenge.replace(/-/g, '+').replace(/_/g, '/')),
        (c) => c.charCodeAt(0)
      );
      const userId = Uint8Array.from(atob(webAuthnOptions.user.id.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
        c.charCodeAt(0)
      );

      const credential = (await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            id: webAuthnOptions.rp.id,
            name: webAuthnOptions.rp.name
          },
          user: {
            id: userId,
            name: webAuthnOptions.user.name,
            displayName: webAuthnOptions.user.displayName
          },
          pubKeyCredParams: webAuthnOptions.pubKeyCredParams.map((p) => ({
            type: 'public-key' as const,
            alg: p.alg
          })),
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required'
          },
          timeout: 60000
        }
      })) as PublicKeyCredential;

      const response = credential.response as AuthenticatorAttestationResponse;
      const publicKeyDer = response.getPublicKey();
      const publicKeyAlgorithm = response.getPublicKeyAlgorithm();
      if (!publicKeyDer) {
        throw new Error(t('Passkey公開鍵の取得に失敗しました', 'Failed to read passkey public key'));
      }

      console.log('Passkey registered:', credential.id);
      setPasskeyCredential({
        id: credential.id,
        publicKeySpki: arrayBufferToBase64(publicKeyDer),
        publicKeyAlgorithm,
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64(response.getAuthenticatorData())
      });
      setStep('passphrase');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('エラーが発生しました', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handlePassphraseSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (passphrase !== confirmPassphrase) {
      setError(t('パスフレーズが一致しません', 'Passphrases do not match'));
      return;
    }

    if (passphrase.length < 12) {
      setError(t('パスフレーズは12文字以上で入力してください', 'Passphrase must be at least 12 characters'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!passkeyCredential) {
        throw new Error(t('Passkey登録を先に完了してください', 'Please complete passkey registration first'));
      }

      const { x25519KeyPair, identityPk, signingPk } = await generateIdentityKeys();
      const identitySkRaw = arrayBufferToBase64(x25519KeyPair.privateKey);
      localStorage.setItem('identitySkRaw', identitySkRaw);
      const { encryptedSk, nonce, salt } = await encryptPrivateKeyWithPassphrase(x25519KeyPair.privateKey, passphrase);

      const result = await api.auth.passkeyRegisterComplete({
        username,
        credential: passkeyCredential,
        encryptedSk,
        nonce,
        argon2Salt: salt,
        identityPk,
        signingPk
      });

      const account = saveOrUpdateAccount({
        userId: result.userId,
        username,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        identitySkRaw
      });
      applyAccountToSession(account);

      console.log('Registration complete:', result.userId);
      navigate('/circles');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('エラーが発生しました', 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (loading) {
      return (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      );
    }

    if (step === 'username') {
      return (
        <form className="space-y-4" onSubmit={handleUsernameSubmit}>
          <div className="space-y-2">
            <Label htmlFor="username">{t('ユーザー名', 'Username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={20}
              placeholder={t('3-20文字で入力', '3-20 characters')}
            />
          </div>
          <Button type="submit" className="w-full">
            {t('次へ', 'Next')}
          </Button>
        </form>
      );
    }

    if (step === 'passkey') {
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              '端末に保存されるPasskeyを登録します。生体認証またはPIN入力を求められます。',
              'Register a passkey stored on your device. You may be asked for biometrics or a PIN.'
            )}
          </p>
          <Button onClick={handlePasskeyRegister} className="w-full">
            {t('Passkeyを登録', 'Register Passkey')}
          </Button>
        </div>
      );
    }

    return (
      <form className="space-y-4" onSubmit={handlePassphraseSubmit}>
        <div className="space-y-2">
          <Label htmlFor="passphrase">{t('パスフレーズ（12文字以上）', 'Passphrase (12+ characters)')}</Label>
          <Input
            id="passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            required
            minLength={12}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassphrase">{t('確認用パスフレーズ', 'Confirm passphrase')}</Label>
          <Input
            id="confirmPassphrase"
            type="password"
            value={confirmPassphrase}
            onChange={(e) => setConfirmPassphrase(e.target.value)}
            required
          />
        </div>
        <Button type="submit" className="w-full">
          {t('アカウント作成', 'Create account')}
        </Button>
      </form>
    );
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md border-border/80">
        <CardHeader>
          <CardTitle>{t('新規登録', 'Register')}</CardTitle>
          <CardDescription>
            {step === 'username' ? t('はじめにユーザー名を設定します。', 'Start by setting your username.') : null}
            {step === 'passkey' ? t('次にPasskeyを端末へ登録します。', 'Next, register a passkey on your device.') : null}
            {step === 'passphrase'
              ? t('最後に秘密鍵保護用パスフレーズを設定します。', 'Finally, set a passphrase to protect your private key.')
              : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            <div className={step === 'username' ? 'text-foreground' : ''}>{t('01 ユーザー名', '01 Username')}</div>
            <div className={step === 'passkey' ? 'text-foreground' : ''}>{t('02 パスキー', '02 Passkey')}</div>
            <div className={step === 'passphrase' ? 'text-foreground' : ''}>{t('03 パスフレーズ', '03 Passphrase')}</div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {renderStep()}

          <p className="text-center text-sm text-muted-foreground">
            {t('すでにアカウントがある場合は', 'Already have an account?')}{' '}
            <Link className="underline decoration-dotted underline-offset-4" to="/login">
              {t('ログイン', 'Login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
